import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal, FloorLabel } from "../motion/Reveal";
import { LOCALE, t } from "../../lib/i18n";

const fmt = (s) => `${s.prefix || ""}${Number(s.value).toLocaleString(LOCALE, { minimumFractionDigits: s.decimals || 0, maximumFractionDigits: s.decimals || 0 })}${s.suffix || ""}`;

/**
 * Navy statement floor – one composition, not four stacked components.
 * Row 1: kicker + title on the left (col 1–6), the two explanatory paragraphs on the right (col 7–11), starting
 * level with the title. Row 2, after a clear pause: the principle on the left, its one link locked to the right
 * edge of the text block. Then a single hairline and four identical, left-aligned figure columns.
 */
export const ManifestoBand = ({ settings = {}, stats = [] }) => {
  const title = settings.manifesto_title || t("Industribyggeren bak sterke fagmiljøer");
  const paragraphs = String(settings.manifesto || "").split(/\n\n+/).filter(Boolean);
  const statement = settings.manifesto_statement || t("Vi bygger for neste tiår – ikke neste kvartal.");
  const figures = (stats || []).slice(0, 4);
  return (
    <section data-testid="floor-manifest" className="bg-navy text-white">
      <div className="wrap py-[clamp(72px,8.5vw,128px)]">
        {/* Who EQVA is – title and explanation side by side */}
        <div className="grid-12 gap-y-10 items-start">
          <Reveal className="col-span-12 lg:col-span-6">
            <FloorLabel light>{t("Dette er EQVA")}</FloorLabel>
            <h2 data-testid="manifesto-title" className="mt-5 text-[clamp(2rem,3.5vw,3.5rem)] font-medium leading-[1.05] tracking-[-0.03em] max-w-[16ch] text-balance">{title}</h2>
          </Reveal>
          <Reveal delay={100} data-testid="manifesto-text" className="col-span-12 lg:col-span-5 lg:col-start-7 lg:pt-[calc(var(--fs-label)*1.2+1.25rem+0.35em)] grid gap-y-6">
            {paragraphs.map((p, i) => <p key={i} className="t-body text-white/80">{p}</p>)}
          </Reveal>
        </div>

        {/* How EQVA thinks – the principle, tied to the explanation above */}
        <Reveal delay={120} className="mt-16 md:mt-[72px] grid-12 gap-y-6 items-end">
          <p data-testid="manifesto-statement" className="col-span-12 lg:col-span-6 text-[clamp(1.75rem,2.4vw,2.375rem)] font-medium leading-[1.15] tracking-[-0.025em] max-w-[22ch] text-balance">{statement}</p>
          <Link to="/om-oss/strategi" data-testid="manifesto-link-strategi" className="link-arrow !text-white t-small col-span-12 lg:col-span-5 lg:col-start-7 lg:justify-self-end mb-1">{t("Les om strategien vår")} <ArrowRight size={16} /></Link>
        </Reveal>

        {figures.length > 0 && (
          <Reveal delay={120} as="dl" data-testid="key-stats" className="mt-16 md:mt-[68px] grid grid-cols-2 lg:grid-cols-4 gap-x-[var(--col-gap)] gap-y-8 border-t border-white/15 pt-8">
            {figures.map((st) => (
              <div key={st.key} data-testid={`key-stat-${st.key}`} className="flex flex-col-reverse">
                <dt className="mt-2 t-small text-white/70">{st.label}</dt>
                <dd className="tabular text-[clamp(2rem,3vw,3.25rem)] font-medium leading-none tracking-[-0.035em]">{fmt(st)}</dd>
              </div>
            ))}
          </Reveal>
        )}
      </div>
    </section>
  );
};
